/**
 * Review decisions from the run's review export (`GET /runs/{id}/review-export`, text/csv) — the
 * only read that carries per-decision verdicts and reviewers (`/review-stats` counts products, not
 * decisions). Pure: parse the CSV into rows, then roll them up by subject × verdict, per QC check
 * and per reviewer. Columns (backend `services/run_review_service.py` `EXPORT_COLUMNS`):
 * `product_id, external_id, subject, component, attribute_name, model_value, verdict,
 * corrected_value, check_name, note, reviewer, reviewed_at`. One row per review item's LATEST
 * decision; WITHDRAWN items are already left out, so every row is a standing decision.
 */
import Papa from 'papaparse';

import type { ReviewDecision, ReviewSubject } from '../types/review.types';
import { REVIEW_DECISIONS, REVIEW_SUBJECTS } from '../types/review.types';

/** A standing decision's verdict — the export never carries WITHDRAWN. */
export type DecisionVerdict = Exclude<ReviewDecision, 'WITHDRAWN'>;

/** One parsed export row (camel-cased; empty cells are ''). */
export interface ReviewDecisionRow {
  productId: string;
  externalId: string;
  /** A known `ReviewSubject`, or the raw text when the service adds a new one. */
  subject: string;
  component: string;
  attributeName: string;
  modelValue: string;
  verdict: string;
  correctedValue: string;
  /** Set on QC-flag decisions (FINDING_CONFIRMED / FINDING_DISMISSED); '' otherwise. */
  checkName: string;
  note: string;
  /** Who decided ('' when the service didn't record one). */
  reviewer: string;
  /** ISO-8601 UTC, or '' when missing. */
  reviewedAt: string;
}

/** A QC flag's standing decision — the export never carries WITHDRAWN (taken back reads as open). */
export type FlagVerdict = 'FINDING_CONFIRMED' | 'FINDING_DISMISSED';

/** Counts per verdict, plus their total. */
export type VerdictCounts = Record<DecisionVerdict, number> & { total: number };

/** The three groups the Summary / Review show: product type, attributes, QC flags. */
export type DecisionGroup = 'type' | 'attributes' | 'qcFlags';

/** One reviewer's share of the decisions. */
export interface ReviewerDecisions {
  /** As exported; '' is shown as "Unknown reviewer" by the UI. */
  reviewer: string;
  decisions: number;
  /** Distinct products this reviewer decided something on. */
  products: number;
  /** Their latest `reviewed_at` (ISO), or null. */
  lastReviewedAt: string | null;
}

/** QC-flag decisions on one check. */
export interface CheckDecisions {
  confirmed: number;
  dismissed: number;
}

/** Everything the UI reads from one export. */
export interface ReviewDecisionSummary {
  /** Standing decisions (rows). */
  total: number;
  /** Distinct products with at least one decision. */
  products: number;
  /** Per wire subject (PRODUCT_TYPE · LISTING_SHAPE · CORE_ATTRIBUTE · COMPONENT_ATTRIBUTE). */
  bySubject: Record<ReviewSubject, VerdictCounts>;
  /**
   * Per UI group: `qcFlags` = rows with a `check_name` (finding decisions, whatever their
   * subject); `type` = PRODUCT_TYPE + LISTING_SHAPE; `attributes` = core + component attributes.
   * A row lands in exactly one group, so the three totals add up to `total`.
   */
  byGroup: Record<DecisionGroup, VerdictCounts>;
  /** QC-flag decisions per check name (`QcCheck` codes; only checks with a decision appear). */
  byCheck: Record<string, CheckDecisions | undefined>;
  /** Reviewers, most decisions first. */
  reviewers: ReviewerDecisions[];
  /** Rows whose subject or verdict the app doesn't know — counted in `total` only. */
  unknown: number;
  /** `qcFlagKey` of every decided QC flag, to tell which findings are still open. */
  decidedFlags: string[];
  /** Each decided QC flag's verdict by `qcFlagKey`, for per-flag status (Real issue / Not an issue). */
  flagVerdicts: Record<string, FlagVerdict>;
}

/**
 * One QC flag's identity, shared by a review-export row and a `/qc-findings` item: the review
 * item is keyed by product, check, component and attribute on the service.
 */
export function qcFlagKey(flag: {
  productId: string;
  checkName: string;
  component: string;
  attributeName: string;
}): string {
  return [flag.productId, flag.checkName, flag.component, flag.attributeName].join('|');
}

/** Export header → row key. */
const COLUMN_KEYS: Record<string, keyof ReviewDecisionRow> = {
  product_id: 'productId',
  external_id: 'externalId',
  subject: 'subject',
  component: 'component',
  attribute_name: 'attributeName',
  model_value: 'modelValue',
  verdict: 'verdict',
  corrected_value: 'correctedValue',
  check_name: 'checkName',
  note: 'note',
  reviewer: 'reviewer',
  reviewed_at: 'reviewedAt',
};

/** First characters the export neutralises with a leading `'` (spreadsheet formula guard). */
const FORMULA_PREFIXES = ['=', '+', '-', '@', '\t', '\r'];

/** Undoes the export's formula guard: `'=x` → `=x`; any other text is returned as-is. */
function unguard(cell: string): string {
  return cell.startsWith("'") && FORMULA_PREFIXES.some((p) => cell.startsWith(p, 1))
    ? cell.slice(1)
    : cell;
}

/** Zeroed counts for every standing verdict. */
function emptyCounts(): VerdictCounts {
  const counts = { total: 0 } as VerdictCounts;
  for (const verdict of REVIEW_DECISIONS) {
    if (verdict !== 'WITHDRAWN') counts[verdict] = 0;
  }
  return counts;
}

/** Zero counts for every group — what a run with no decisions yet reads as (no export needed). */
export function emptyDecisionGroups(): Record<DecisionGroup, VerdictCounts> {
  return { type: emptyCounts(), attributes: emptyCounts(), qcFlags: emptyCounts() };
}

const SUBJECT_SET: ReadonlySet<string> = new Set(REVIEW_SUBJECTS);
const VERDICT_SET: ReadonlySet<string> = new Set(
  REVIEW_DECISIONS.filter((verdict) => verdict !== 'WITHDRAWN'),
);

/**
 * Parses the export's CSV text (RFC 4180, header row first) into rows. Unknown columns are
 * ignored, missing ones read as ''; a header-only export (nothing reviewed) is `[]`.
 */
export function parseReviewExportCsv(text: string): ReviewDecisionRow[] {
  const parsed = Papa.parse<Record<string, string>>(
    text.charCodeAt(0) === 0xfeff ? text.slice(1) : text,
    {
      header: true,
      skipEmptyLines: true,
      transformHeader: (header) => header.trim().toLowerCase(),
    },
  );
  return parsed.data.map((record) => {
    const row: ReviewDecisionRow = {
      productId: '',
      externalId: '',
      subject: '',
      component: '',
      attributeName: '',
      modelValue: '',
      verdict: '',
      correctedValue: '',
      checkName: '',
      note: '',
      reviewer: '',
      reviewedAt: '',
    };
    for (const [column, key] of Object.entries(COLUMN_KEYS)) {
      const cell = record[column];
      // Papaparse leaves a short row's missing cells undefined.
      if (cell !== undefined) row[key] = unguard(cell.trim());
    }
    return row;
  });
}

/** Which UI group a row counts under (see `ReviewDecisionSummary.byGroup`). */
function decisionGroup(row: Pick<ReviewDecisionRow, 'subject' | 'checkName'>): DecisionGroup {
  if (row.checkName !== '') return 'qcFlags';
  if (row.subject === 'PRODUCT_TYPE' || row.subject === 'LISTING_SHAPE') return 'type';
  return 'attributes';
}

/**
 * Rolls parsed rows up into counts by subject × verdict, by UI group, per QC check
 * (confirmed / dismissed) and per reviewer. Rows with an unknown subject or verdict still count
 * toward `total`, `products` and their reviewer, and are tallied in `unknown`.
 */
export function summarizeReviewDecisions(
  rows: readonly ReviewDecisionRow[],
): ReviewDecisionSummary {
  const bySubject = Object.fromEntries(
    REVIEW_SUBJECTS.map((subject) => [subject, emptyCounts()]),
  ) as Record<ReviewSubject, VerdictCounts>;
  const byGroup: Record<DecisionGroup, VerdictCounts> = {
    type: emptyCounts(),
    attributes: emptyCounts(),
    qcFlags: emptyCounts(),
  };
  const byCheck: ReviewDecisionSummary['byCheck'] = {};
  const people = new Map<
    string,
    { decisions: number; products: Set<string>; last: string | null }
  >();
  const products = new Set<string>();
  const decidedFlags = new Set<string>();
  const flagVerdicts: Record<string, FlagVerdict> = {};
  let unknown = 0;

  for (const row of rows) {
    if (row.productId) products.add(row.productId);
    const person = people.get(row.reviewer) ?? { decisions: 0, products: new Set(), last: null };
    person.decisions += 1;
    if (row.productId) person.products.add(row.productId);
    // ISO-8601 UTC strings compare correctly as text.
    if (row.reviewedAt && (person.last === null || row.reviewedAt > person.last)) {
      person.last = row.reviewedAt;
    }
    people.set(row.reviewer, person);

    if (!VERDICT_SET.has(row.verdict) || !SUBJECT_SET.has(row.subject)) {
      unknown += 1;
      continue;
    }
    const verdict = row.verdict as DecisionVerdict;
    const subject = bySubject[row.subject as ReviewSubject];
    subject[verdict] += 1;
    subject.total += 1;
    const group = byGroup[decisionGroup(row)];
    group[verdict] += 1;
    group.total += 1;
    if (
      row.checkName !== '' &&
      (verdict === 'FINDING_CONFIRMED' || verdict === 'FINDING_DISMISSED')
    ) {
      const flagKey = qcFlagKey(row);
      decidedFlags.add(flagKey);
      flagVerdicts[flagKey] = verdict;
      const check = byCheck[row.checkName] ?? { confirmed: 0, dismissed: 0 };
      if (verdict === 'FINDING_CONFIRMED') check.confirmed += 1;
      else check.dismissed += 1;
      byCheck[row.checkName] = check;
    }
  }

  const reviewers = [...people.entries()]
    .map(([reviewer, p]) => ({
      reviewer,
      decisions: p.decisions,
      products: p.products.size,
      lastReviewedAt: p.last,
    }))
    .sort((a, b) => b.decisions - a.decisions || a.reviewer.localeCompare(b.reviewer));

  return {
    total: rows.length,
    products: products.size,
    bySubject,
    byGroup,
    byCheck,
    reviewers,
    unknown,
    decidedFlags: [...decidedFlags],
    flagVerdicts,
  };
}

/** Parse + roll up in one step — what `useReviewDecisions` caches. */
export function parseReviewDecisions(text: string): ReviewDecisionSummary {
  return summarizeReviewDecisions(parseReviewExportCsv(text));
}

/**
 * One CSV of the decisions on `productIds` from several runs' exports (a reused check's rows live
 * on the runs that wrote them): header from the first export, rows of other products dropped.
 */
export function filterReviewExports(
  texts: readonly string[],
  productIds: ReadonlySet<string>,
): string {
  let fields: string[] = [];
  const records: Record<string, string>[] = [];
  for (const text of texts) {
    const parsed = Papa.parse<Record<string, string>>(
      text.charCodeAt(0) === 0xfeff ? text.slice(1) : text,
      { header: true, skipEmptyLines: true, transformHeader: (header) => header.trim() },
    );
    if (fields.length === 0) fields = parsed.meta.fields ?? [];
    for (const record of parsed.data) {
      if (productIds.has((record.product_id ?? '').trim())) records.push(record);
    }
  }
  return Papa.unparse({
    fields,
    data: records.map((record) => fields.map((f) => record[f] ?? '')),
  });
}

/** "Priya N. 28 products · Samben J. 12 products" — reviewers by products decided. */
export function reviewersLine(reviewers: readonly ReviewerDecisions[]): string {
  return reviewers
    .map(
      (r) =>
        `${r.reviewer || 'Unknown reviewer'} ${r.products.toLocaleString()} ${r.products === 1 ? 'product' : 'products'} · ${r.decisions.toLocaleString()} ${r.decisions === 1 ? 'decision' : 'decisions'}`,
    )
    .join(' · ');
}
